import { useMsal } from "@azure/msal-react";
import {
    Anchor,
    Box,
    Button,
    Divider,
    Flex,
    Group,
    Loader,
    Modal,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconExternalLink } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { useMemo, useRef, useState } from "react";
import { DatasourceBadges } from "src/components/adminBadges";
import { useAuthSearchAll, useAuthSearchPagination } from "src/hooks/useAuthSearchPagination";
import { fetchWithAuth, getAPI } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";
import {
    CopyableField,
    RoleSelectionComponent,
    scdPersonUrl,
    type User,
    useUserOperations,
} from "./manageUsers";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

export type Request = {
    id: string;
    user_id: string;
    user_email: string;
    request_datetime: number;
    requested_datasources: { id: string }[];
    status: string;
    reason: string;
};

interface QueryParams {
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
}

interface UpdateRequestStatusParams {
    user_id: string;
    updated_status: string;
    rejection_reason: string | null;
}

interface Role {
    id: string;
    description: string;
    datasources: { id: string }[];
}

// Create a custom hook for request operations
const useRequestOperations = (params: QueryParams) => {
    const isAdmin = useAdmin();
    const { instance } = useMsal();
    const queryClient = useQueryClient();
    const { columnFilters, globalFilter } = params;

    const requestsQuery = useAuthSearchPagination<Request>({
        queryKey: ["access-requests"],
        path: "v1/auth/access-requests/search",
        columnFilters,
        globalFilter,
        globalFilterKey: "user_email",
        maxResults: 25,
        enabled: isAdmin,
    });

    // PATCH operation
    const updateRequest = async ({
        user_id,
        updated_status,
        rejection_reason,
    }: UpdateRequestStatusParams) => {
        const response = await fetchWithAuth({
            url: `${BACKEND_URL}/v1/auth/access-requests`,
            method: "PATCH",
            payload: { user_id, updated_status, rejection_reason },
            instance,
        });

        if (!response?.ok) {
            throw new Error("Failed to update request status");
        }

        queryClient.invalidateQueries({ queryKey: ["access-requests"] });

        return response;
    };

    return { requestsQuery, updateRequest };
};

const useRoles = () =>
    useAuthSearchAll<Role>({
        queryKey: ["roles"],
        path: "v1/auth/roles/search",
    });

// Requests can reference any user, not just the first page, so fetch the full user list.
const useAllUsers = () =>
    useAuthSearchAll<User>({
        queryKey: ["users", "all"],
        path: "v1/auth/users/search",
    });

type AuthDatasource = { id: string; description?: string; name?: string };

const useAuthDatasources = () => {
    const { instance } = useMsal();

    return useQuery<AuthDatasource[], Error>({
        queryKey: ["auth-datasources"],
        queryFn: async () => {
            const response = await getAPI({
                url: `${BACKEND_URL}/v1/auth/datasources`,
                instance,
            });
            const jsonData = await response?.json();
            if (Array.isArray(jsonData)) return jsonData;
            if (Array.isArray(jsonData?.data)) return jsonData.data;
            return [];
        },
    });
};

const ManageRequests = () => {
    const { data: roles, isLoading: isLoadingRoles, refetch: fetchRoles } = useRoles();
    const { data: authDatasources = [] } = useAuthDatasources();
    // Use stable empty array reference to prevent unnecessary re-renders
    const stableRoles = useMemo(() => roles ?? [], [roles]);

    const datasourceDescriptions = useMemo(() => {
        const list = Array.isArray(authDatasources) ? authDatasources : [];
        return Object.fromEntries(list.map((ds) => [ds.id, ds.description || ds.name || ""]));
    }, [authDatasources]);

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([
        { id: "status", value: "pending" },
    ]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);

    const { updateUser } = useUserOperations({
        columnFilters: [],
        globalFilter: "",
        enabled: false,
    });

    const { data: allUsers, isLoading: isLoadingUsers } = useAllUsers();
    const fetchedUsers = useMemo(() => allUsers ?? [], [allUsers]);

    const columns = useMemo<MRT_ColumnDef<Request>[]>(
        () => [
            {
                id: "scd_link",
                header: "",
                accessorFn: (row) => row?.user_id ?? "",
                Cell: ({ cell }) => {
                    const id = cell.getValue<string>();
                    if (!id) return null;
                    return (
                        <Anchor
                            href={scdPersonUrl(id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Open ${id} in SCD`}
                            style={{ cursor: "pointer" }}
                        >
                            <IconExternalLink size={16} />
                        </Anchor>
                    );
                },
                enableSorting: false,
                enableColumnFilter: false,
                enableClickToCopy: false,
                size: 40,
                maxSize: 40,
                minSize: 40,
                enableResizing: false,
            },
            {
                accessorKey: "user_id" as const,
                header: "User ID",
                accessorFn: (row) => row?.user_id ?? "",
            },
            {
                accessorKey: "user_email" as const,
                header: "User Email",
                accessorFn: (row) => row?.user_email ?? "",
            },
            {
                accessorKey: "org_code" as const,
                header: "Org Code",
                accessorFn: (row) =>
                    fetchedUsers.find((user) => user.id === row?.user_id)?.org_code ?? "",
            },
            {
                accessorKey: "request_datetime" as const,
                header: "Requested at",
                accessorFn: (row) =>
                    row ? new Date(1000 * row.request_datetime).toLocaleString() : "",
            },
            {
                accessorKey: "status" as const,
                header: "Status",
                accessorFn: (row) => row?.status ?? "",
            },
        ],
        [fetchedUsers],
    );

    const {
        requestsQuery: {
            data: requests,
            isLoading: isLoadingRequests,
            isFetching,
            isFetchingNextPage,
            hasNextPage,
            fetchNextPage,
            refetch,
        },
        updateRequest,
    } = useRequestOperations({
        columnFilters,
        globalFilter,
    });

    const fetchedRequests = useMemo(() => {
        if (!requests?.pages) return [];
        const seen = new Set<string>();
        return requests.pages
            .flatMap((page) => page.hits.hits)
            .filter((hit) => {
                if (seen.has(hit._id)) return false;
                seen.add(hit._id);
                return true;
            })
            .map((hit) => hit._source);
    }, [requests]);

    const totalRequests = requests?.pages[0]?.hits.total.value ?? fetchedRequests.length;

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement) => {
        if (containerRefElement) {
            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            if (
                scrollHeight - scrollTop - clientHeight < 400 &&
                !isFetchingNextPage &&
                hasNextPage
            ) {
                fetchNextPage();
            }
        }
    };

    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);

    const [modalOpened, setModalOpened] = useState(false);
    const [isSubmittingApprove, setIsSubmittingApprove] = useState(false);
    const [isSubmittingReject, setIsSubmittingReject] = useState(false);

    const [roleSearchTerm, setRoleSearchTerm] = useState("");

    const [rejectRequestModalOpened, { open: openRejectRequest, close: closeRejectRequest }] =
        useDisclosure(false);
    const [rejectionReason, setRejectionReason] = useState<string>("");

    const form = useForm({
        initialValues: {
            user_id: "",
            user_email: "",
            request_datetime: 0,
            requested_datasources: [] as { id: string }[],
            status: "",
            reason: "",
            roles: [] as string[],
        },
        validate: {
            roles: (value) => (value.length === 0 ? "At least one role is required" : null),
        },
    });

    const handleOpenModal = (request: Request) => {
        if (isLoadingRoles) {
            notifications.show({
                title: "Loading",
                message: "Please wait while roles are being loaded...",
                loading: true,
            });
            return;
        }

        if (stableRoles.length === 0) {
            fetchRoles();
        }

        const user = fetchedUsers.find((u: User) => u.id === request.user_id);
        if (!user) {
            notifications.show({
                title: "Error",
                message: "User not found for this request",
                color: "red",
            });
            return;
        }

        setSelectedRequest(request);
        setSelectedUser(user);

        form.setValues({
            user_id: user.id,
            user_email: user.email,
            request_datetime: request.request_datetime,
            requested_datasources: request.requested_datasources,
            status: request.status,
            reason: request.reason,
            roles: user.roles,
        });
        setModalOpened(true);
    };

    const handleApprove = async (user_id: string, roleKeys: string[]) => {
        setIsSubmittingApprove(true);
        try {
            if (!selectedUser) {
                throw new Error("No user selected");
            }

            const updatedUser = await updateUser({
                data: {
                    id: selectedUser.id,
                    email: selectedUser.email,
                    org_code: selectedUser.org_code,
                    roles: roleKeys,
                },
            });

            setSelectedUser(updatedUser);

            const userIndex = fetchedUsers.findIndex((u: User) => u.id === updatedUser.id);
            if (userIndex !== -1) {
                const updatedUsers = [...fetchedUsers];
                updatedUsers[userIndex] = updatedUser;
            }
            await updateRequest({
                user_id: user_id,
                updated_status: "approved",
                rejection_reason: null,
            });
            notifications.show({
                title: "Success",
                message: "Request approved successfully",
                color: "green",
            });
            refetch();
            setModalOpened(false);
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to approve the request",
                color: "red",
            });
        } finally {
            setIsSubmittingApprove(false);
        }
    };

    const handleReject = async (user_id: string) => {
        setIsSubmittingReject(true);
        closeRejectRequest();
        try {
            if (!selectedUser) {
                throw new Error("No user selected");
            }
            await updateRequest({
                user_id: user_id,
                updated_status: "rejected",
                rejection_reason: rejectionReason,
            });
            notifications.show({
                title: "Success",
                message: "Request rejected successfully",
                color: "green",
            });
            refetch();
            setModalOpened(false);
            setRejectionReason("");
            closeRejectRequest();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to reject the request",
                color: "red",
            });
        } finally {
            setIsSubmittingReject(false);
        }
    };

    const table = useMantineReactTable<Request>({
        layoutMode: "grid",
        columns,
        data: fetchedRequests ?? [],
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        renderTopToolbarCustomActions: () => (
            <Flex align="center" gap="md">
                <Text size="sm" c="dimmed">
                    {`Total: ${totalRequests.toLocaleString()}, (Showing ${table.getFilteredRowModel().rows.length.toLocaleString()})`}
                </Text>
                {isFetching && <Loader size="xs" />}
            </Flex>
        ),
        enableColumnResizing: true,
        enableFilters: true,
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row: Request) => row.id,
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableDensityToggle: false,
        enableStickyHeader: true,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { maxHeight: "calc(100vh - 280px)" },
            onScroll: (event) => fetchMoreOnBottomReached(event.currentTarget),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleOpenModal(row.original),
            sx: { cursor: "pointer" },
        }),
        state: {
            isLoading: isLoadingRequests || isLoadingRoles || isLoadingUsers,
            showProgressBars: isFetchingNextPage,
            columnFilters,
            globalFilter,
            sorting,
        },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
    });
    return (
        <>
            <Flex px="xl" pb="xl" direction="column" gap="md">
                <Flex gap="md" align="center">
                    <Title lh={2} c="purple.8" order={2} style={{ textTransform: "uppercase" }}>
                        Manage Requests
                    </Title>
                </Flex>
            </Flex>

            <MantineReactTable table={table} />
            <Modal
                opened={modalOpened}
                onClose={() => {
                    setModalOpened(false);
                    form.reset();
                    setRoleSearchTerm("");
                }}
                title={`Handle User request: ${selectedUser?.email || ""}`}
                size="xl"
                styles={{
                    body: {
                        minHeight: 500,
                    },
                }}
            >
                <form>
                    <Stack gap="md">
                        <SimpleGrid cols={3}>
                            <CopyableField label="Email" value={selectedUser?.email || ""} />
                            <CopyableField
                                label="User ID"
                                value={selectedUser?.id || selectedRequest?.user_id || ""}
                                externalHref={
                                    selectedUser?.id || selectedRequest?.user_id
                                        ? scdPersonUrl(
                                              selectedUser?.id || selectedRequest?.user_id || "",
                                          )
                                        : undefined
                                }
                            />
                            <CopyableField label="Org Code" value={selectedUser?.org_code || ""} />
                        </SimpleGrid>

                        <Divider />

                        <Flex gap="md" align="stretch">
                            <Stack flex={1} gap="md" data-testid="request-details-panel">
                                <SimpleGrid cols={2}>
                                    <TextInput
                                        label="Requested At"
                                        value={
                                            new Date(
                                                1000 * (selectedRequest?.request_datetime || 0),
                                            )?.toLocaleString() || ""
                                        }
                                        readOnly
                                    />
                                    <TextInput
                                        label="Status"
                                        value={selectedRequest?.status || ""}
                                        readOnly
                                    />
                                </SimpleGrid>
                                <Textarea
                                    label="Reason"
                                    value={selectedRequest?.reason || ""}
                                    readOnly
                                    autosize
                                    minRows={2}
                                />
                                {selectedRequest?.requested_datasources?.length > 0 && (
                                    <Box>
                                        <Text size="sm" fw={500} mb={4}>
                                            Requested Datasources
                                        </Text>
                                        <DatasourceBadges
                                            items={selectedRequest.requested_datasources.map(
                                                (ds) => ds.id,
                                            )}
                                            descriptions={datasourceDescriptions}
                                        />
                                    </Box>
                                )}
                            </Stack>

                            <Divider orientation="vertical" />

                            <Box flex={1} data-testid="request-roles-panel">
                                <RoleSelectionComponent
                                    form={form}
                                    roleSearchTerm={roleSearchTerm}
                                    setRoleSearchTerm={setRoleSearchTerm}
                                />
                            </Box>
                        </Flex>

                        <Group justify="flex-end" mt="md">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setModalOpened(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="green"
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    handleApprove(selectedRequest?.user_id || "", form.values.roles)
                                }
                                loading={isSubmittingApprove}
                            >
                                Approve
                            </Button>
                            <Button
                                color="red"
                                type="button"
                                variant="outline"
                                onClick={openRejectRequest}
                                loading={isSubmittingReject}
                            >
                                Reject
                            </Button>
                            <Modal
                                size="lg"
                                opened={rejectRequestModalOpened}
                                onClose={closeRejectRequest}
                                title="Provide a rejection reason"
                            >
                                <Stack align="flex-end">
                                    <Textarea
                                        placeholder="Please as an admin explain why you are rejecting this request"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        style={{ width: "100%" }}
                                        minRows={4}
                                        required
                                    />
                                    <Button
                                        onClick={() => handleReject(selectedRequest?.user_id || "")}
                                        disabled={!rejectionReason.trim()}
                                        color="red"
                                    >
                                        Reject
                                    </Button>
                                </Stack>
                            </Modal>
                        </Group>
                    </Stack>
                </form>
            </Modal>
        </>
    );
};

export default ManageRequests;
